// Phone-number cell with a server-initiated bridged-call button.
//
// Clicking 📞 Call POSTs to /api/voice/click-to-call. The backend places
// an outbound Twilio call to the staff member's SIP endpoint
// (sip:<localpart>@breeze.sip.twilio.com) — staff's softphone rings first,
// then Twilio bridges the customer in with Breeze's main number as
// caller ID. The bridged call is recorded and the audio attaches to the
// customer's card timeline automatically once the call ends.
//
// The number itself is shown as plain text (not a link) so an accidental
// click can't dial; the explicit pill button is the only call affordance.
import { useState } from "react";
import { initiateCall } from "../api.js";

export function PhoneLink({
  number,
  className,
  children,
}: {
  number: string;
  className?: string;
  children?: React.ReactNode;
}): JSX.Element | null {
  const [calling, setCalling] = useState<"idle" | "calling" | "ringing" | "error">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!number || !number.trim()) return null;

  async function handleCall(e: React.MouseEvent<HTMLButtonElement>): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    if (calling !== "idle") return;
    setErrorMsg(null);
    setCalling("calling");
    try {
      const res = await initiateCall(number);
      // Twilio call statuses: queued | ringing | in-progress | completed | …
      // queued/ringing both mean it's now reaching the staff phone.
      setCalling("ringing");
      setTimeout(() => setCalling("idle"), 2500);
      void res; // currently we don't surface CallSid in the UI
    } catch (err) {
      const msg = err instanceof Error ? err.message : "call failed";
      setErrorMsg(msg);
      setCalling("error");
      setTimeout(() => {
        setCalling("idle");
        setErrorMsg(null);
      }, 4000);
    }
  }

  const labelByState: Record<typeof calling, string> = {
    idle: "📞 Call",
    calling: "Calling…",
    ringing: "Ringing your phone…",
    error: "Failed",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${className ?? ""}`}
      title={
        calling === "idle"
          ? `Click 📞 Call to ring your softphone, then bridge ${number}`
          : errorMsg ?? labelByState[calling]
      }
    >
      <span className="text-ink-50">{children ?? number}</span>
      <button
        onClick={(e) => void handleCall(e)}
        disabled={calling !== "idle"}
        className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
          calling === "error"
            ? "border-slack-pink/60 bg-slack-pink/15 text-slack-pink"
            : calling === "idle"
              ? "border-slack-cyan/50 bg-slack-cyan/10 text-slack-cyan hover:bg-slack-cyan/20"
              : "border-ink-500 bg-ink-800 text-ink-200"
        } disabled:cursor-not-allowed`}
      >
        {labelByState[calling]}
      </button>
    </span>
  );
}
